import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Baby, FileText, Calendar, Send, Save, ClipboardCheck, Edit2, LogIn, LogOut, XCircle, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import StudentAvatar from './StudentAvatar';
import CnsPyramid from './CnsPyramid';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMalaysiaTimeOnly = (value) => {
  if (!value) return '—';
  // If it's already a simple HH:MM or HH:MM:SS string, return as-is
  if (/^\d{2}:\d{2}(?::\d{2})?$/.test(value)) return value;
  try {
    return new Date(value).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' });
  } catch (e) {
    return value;
  }
};

const statusBadge = (status) => {
  const map = {
    pending: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    approved: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    rejected: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
    present: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    absent: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    late: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    leave: 'bg-blue-100 text-blue-700 border-blue-300',
  };
  return <Badge className={`${map[status] || 'bg-slate-100 text-slate-700 border-slate-300'} border`}>{status}</Badge>;
};

const emptyReport = {
  student_id: '', month: new Date().toISOString().slice(0, 7),
  highlights: '', special_event: '',
  therapy_notes: '', areas_of_improvement: '', recommendations: '',
  overall_rating: 3,
  cns_ratings: {},
};

const emptyAttendance = { student_id: '', date: new Date().toISOString().slice(0, 10), status: 'present', time_in: '', time_out: '', note: '' };

const TeacherStudentsSection = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [reports, setReports] = useState([]);
  const [otReports, setOtReports] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [attendance, setAttendance] = useState([]);
  const [activeTab, setActiveTab] = useState('attendance');
  const [reportDialog, setReportDialog] = useState(false);
  const [otReportDialog, setOtReportDialog] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editingOtReport, setEditingOtReport] = useState(null);
  const [reportForm, setReportForm] = useState(emptyReport);
  const [attendanceForm, setAttendanceForm] = useState(emptyAttendance);
  const [otReportForm, setOtReportForm] = useState({
    student_id: '', ot_session_start_date: '', sessions_attended: '',
    current_condition_attention: '', current_condition_fine_motor: '', current_condition_gross_motor: '',
    current_condition_sensory: '', current_condition_adl: '', current_condition_behaviour: '',
    short_term_goals: [], progress_3_months: '', next_3_month_plan: '', home_program: [],
    progress_meter: {},
  });
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async (studentId) => {
    try {
      const { data } = await axios.get(`${API}/api/reports`, {
        params: { student_id: studentId },
        withCredentials: true,
      });
      setReports(data);
    } catch (e) {
      toast.error('Failed to load reports');
    }
  }, []);

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/api/reports/${id}`, { withCredentials: true });
      toast.success('Report deleted');
      fetchReports(selectedStudentId);
    } catch (e) {
      toast.error('Failed to delete report');
    }
  };

  const fetchOtReports = useCallback(async (studentId) => {
    try {
      const { data } = await axios.get(`${API}/api/ot-reports`, {
        params: { student_id: studentId },
        withCredentials: true,
      });
      setOtReports(data);
    } catch (e) {
      toast.error('Failed to load OT reports');
    }
  }, []);

  const deleteOtReport = async (id) => {
    if (!window.confirm('Delete this OT report?')) return;
    try {
      await axios.delete(`${API}/api/ot-reports/${id}`, { withCredentials: true });
      toast.success('OT report deleted');
      fetchOtReports(selectedStudentId);
    } catch (e) {
      toast.error('Failed to delete OT report');
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/students`, { withCredentials: true });
      setStudents(data);
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teacherLocation = user?.department || '';
  const studentLocations = useMemo(
    () => [...new Set(students.filter((s) => s.location).map((s) => s.location))].sort(),
    [students]
  );
  const locationOptions = useMemo(
    () => (teacherLocation
      ? [teacherLocation, ...studentLocations.filter((loc) => loc !== teacherLocation)]
      : studentLocations),
    [teacherLocation, studentLocations]
  );

  useEffect(() => {
    if (teacherLocation && !selectedLocation && locationOptions.includes(teacherLocation)) {
      setSelectedLocation(teacherLocation);
    } else if (!teacherLocation && !selectedLocation && locationOptions.length === 1) {
      setSelectedLocation(locationOptions[0]);
    }
  }, [teacherLocation, locationOptions, selectedLocation]);

  useEffect(() => {
    if (selectedStudentId) {
      fetchReports(selectedStudentId);
      fetchOtReports(selectedStudentId);
    }
  }, [selectedStudentId, fetchReports, fetchOtReports]);

  useEffect(() => {
    if (activeTab === 'attendance') {
      // For attendance tab, location selection is required
      if (!selectedLocation) {
        setSelectedStudentId('');
        return;
      }
      const matchingStudents = students.filter((s) => s.location === selectedLocation);
      if (matchingStudents.length === 0) {
        setSelectedStudentId('');
      } else if (!matchingStudents.some((s) => s.id === selectedStudentId)) {
        setSelectedStudentId(matchingStudents[0].id);
      }
    } else if (activeTab === 'reports' || activeTab === 'ot-reports') {
      // For reports and OT reports tabs, location is not required - allow selecting any student
      if (selectedStudentId && !students.some((s) => s.id === selectedStudentId)) {
        setSelectedStudentId('');
      } else if (!selectedStudentId && students.length > 0) {
        setSelectedStudentId(students[0].id);
      }
    }
  }, [selectedLocation, students, selectedStudentId, activeTab]);

  const fetchAttendance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/attendance`, { params: { student_id: selectedStudentId, month: currentMonth }, withCredentials: true });
      setAttendance(data);
    } catch (e) { setAttendance([]); }
  }, [selectedStudentId, currentMonth]);

  useEffect(() => { if (selectedStudentId) fetchAttendance(); }, [selectedStudentId, currentMonth, fetchAttendance]);


  const openCreateReport = (studentId) => {
    setEditingReport(null);
    setReportForm({ ...emptyReport, student_id: studentId });
    setReportDialog(true);
  };

  const openEditReport = (report) => {
    setEditingReport(report);
    setReportForm({ ...report });
    setReportDialog(true);
  };

  const saveReport = async (submitForApproval) => {
    try {
      if (editingReport) {
        const status = submitForApproval ? 'pending' : reportForm.status || 'draft';
        await axios.put(`${API}/api/reports/${editingReport.id}`, { ...reportForm, status }, { withCredentials: true });
        toast.success(submitForApproval ? 'Report submitted for approval' : 'Report saved as draft');
      } else {
        await axios.post(`${API}/api/reports`, { ...reportForm, submit_for_approval: submitForApproval }, { withCredentials: true });
        toast.success(submitForApproval ? 'Report submitted for approval' : 'Report saved as draft');
      }
      setReportDialog(false);
      if (selectedStudentId) fetchReports(selectedStudentId);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save report';
      toast.error(typeof message === 'string' ? message : 'Failed to save report');
    }
  };

  const openCreateOtReport = (studentId) => {
    setEditingOtReport(null);
    setOtReportForm({
      student_id: studentId, ot_session_start_date: '', sessions_attended: '',
      current_condition_attention: '', current_condition_fine_motor: '', current_condition_gross_motor: '',
      current_condition_sensory: '', current_condition_adl: '', current_condition_behaviour: '',
      short_term_goals: [], progress_3_months: '', next_3_month_plan: '', home_program: [],
      progress_meter: {},
    });
    setOtReportDialog(true);
  };

  const openEditOtReport = (report) => {
    setEditingOtReport(report);
    setOtReportForm({ ...report });
    setOtReportDialog(true);
  };

  const saveOtReport = async (submitForApproval) => {
    try {
      if (editingOtReport) {
        const status = submitForApproval ? 'pending' : otReportForm.status || 'draft';
        await axios.put(`${API}/api/ot-reports/${editingOtReport.id}`, { ...otReportForm, status }, { withCredentials: true });
        toast.success(submitForApproval ? 'OT report submitted for approval' : 'OT report saved as draft');
      } else {
        await axios.post(`${API}/api/ot-reports`, { ...otReportForm, submit_for_approval: submitForApproval }, { withCredentials: true });
        toast.success(submitForApproval ? 'OT report submitted for approval' : 'OT report saved as draft');
      }
      setOtReportDialog(false);
      if (selectedStudentId) fetchOtReports(selectedStudentId);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save OT report';
      toast.error(typeof message === 'string' ? message : 'Failed to save OT report');
    }
  };

  const markAttendance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/attendance`, { ...attendanceForm, student_id: selectedStudentId }, { withCredentials: true });
      toast.success('Attendance marked');
      setAttendanceForm({ ...emptyAttendance, date: attendanceForm.date });
      fetchAttendance();
    } catch (e) { toast.error('Failed to mark attendance'); }
  };

  const currentTime = () => {
    // Return current time in Malaysia local time (HH:MM)
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kuala_Lumpur' }).formatToParts(new Date());
      const h = parts.find(p => p.type === 'hour')?.value || '00';
      const m = parts.find(p => p.type === 'minute')?.value || '00';
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    } catch (e) {
      const d = new Date();
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
  };

  const quickMark = async (kind) => {
    if (!selectedStudentId) return;
    const today = new Date().toISOString().slice(0, 10);
    const now = currentTime();
    const existing = attendance.find((a) => a.date === today);
    const payload = {
      student_id: selectedStudentId,
      date: today,
      status: kind === 'absent' ? 'absent' : (existing?.status || 'present'),
      time_in: kind === 'in' ? now : (kind === 'absent' ? '' : existing?.time_in || ''),
      time_out: kind === 'out' ? now : (kind === 'absent' ? '' : existing?.time_out || ''),
      note: kind === 'absent' ? 'Marked absent by teacher' : existing?.note || '',
    };
    if (kind === 'in') payload.time_in = now;
    if (kind === 'out') payload.time_out = now;
    try {
      await axios.post(`${API}/api/attendance`, payload, { withCredentials: true });
      const message = kind === 'absent' ? 'Marked absent for today' : `${kind === 'in' ? 'Arrival' : 'Departure'} marked at ${now}`;
      toast.success(message);
      fetchAttendance();
    } catch (e) { toast.error('Failed to mark'); }
  };

  const filteredStudentsForLocation = selectedLocation ? students.filter((s) => s.location === selectedLocation) : [];
  const filteredStudents = activeTab === 'attendance' ? filteredStudentsForLocation : students;
  const selectedStudent = students.find((s) => s.id === selectedStudentId);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  if (students.length === 0) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="p-12 text-center text-slate-500">
          <Baby className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p>No students in the system yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="teacher-students-section">
      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto">
          <TabsTrigger value="attendance" data-testid="tab-attendance-inner">Attendance</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports-inner">Reports</TabsTrigger>
          <TabsTrigger value="ot-reports" data-testid="tab-ot-reports-inner">OT Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="attendance">
      {/* Student selector */}
      <div className="flex flex-col gap-4">
        {/* Contextual hint/banner */}
        {(!selectedLocation || filteredStudentsForLocation.length === 0) && (
          <div className="p-3 rounded-md text-sm" style={{ background: selectedLocation ? '#EFF6FF' : '#FFFBEB', border: '1px solid', borderColor: selectedLocation ? '#BFDBFE' : '#FDE68A', color: selectedLocation ? '#0F172A' : '#92400E' }}>
            {!selectedLocation ? (
              <div>
                <strong className="block">Select a location</strong>
                <span className="text-xs">Choose a location from the dropdown to list students assigned to that location.</span>
              </div>
            ) : (
              <div>
                <strong className="block">No students in this location</strong>
                <span className="text-xs">There are no students registered for the selected location. Try another location or contact administration.</span>
              </div>
            )}
          </div>
        )}
          <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm font-medium">Location:</Label>
          <Select value={selectedLocation || undefined} onValueChange={(value) => setSelectedLocation(value)}>
            <SelectTrigger className="min-w-[220px]" data-testid="teacher-location-select">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locationOptions.length === 0 ? (
                <SelectItem value="__none__" disabled>No location available</SelectItem>
              ) : (
                locationOptions.map((loc) => (
                  <SelectItem key={loc} value={loc} data-testid={`teacher-location-option-${loc}`}>
                    {loc}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-sm font-medium">Students:</Label>
            <div className="flex flex-wrap gap-2">
              {filteredStudents.length === 0 ? (
                <div className="text-sm text-slate-500">
                  {students.length === 0
                    ? 'No students in the system yet.'
                    : (!selectedLocation ? 'Select a location first' : 'No students found for this location')}
                </div>
              ) : filteredStudents.map((s) => (
                <Button
                  key={s.id}
                  variant={selectedStudentId === s.id ? 'default' : 'outline'}
                  size="sm"
                  className={`${selectedStudentId === s.id ? 'bg-blue-600 hover:bg-blue-700' : ''} flex items-center gap-2`}
                  onClick={() => setSelectedStudentId(s.id)}
                  data-testid={`teacher-student-select-${s.id}`}
                >
                  <StudentAvatar photoId={s.student_photo_id} name={s.name} size="sm" testId={`teacher-student-avatar-${s.id}`} />
                  {s.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Attendance — {selectedStudent?.name}</CardTitle>
                <CardDescription>Today: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</CardDescription>
              </div>
              <Input type="month" value={currentMonth} onChange={(e) => setCurrentMonth(e.target.value)} className="w-40" />
            </div>
          </CardHeader>
          <CardContent>
              {attendance.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm">No records yet — mark today's attendance below</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Time In</TableHead><TableHead>Time Out</TableHead><TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="teacher-attendance-table">
                    {attendance.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.date}</TableCell>
                        <TableCell>{statusBadge(a.status)}</TableCell>
                        <TableCell>{formatMalaysiaTimeOnly(a.time_in)}</TableCell>
                        <TableCell>{formatMalaysiaTimeOnly(a.time_out)}</TableCell>
                        <TableCell className="text-sm text-slate-600">{a.note || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Mark attendance form */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Mark Attendance</CardTitle>
              <CardDescription>Quick mark or fill in manually</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Quick actions */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="quick-mark-panel">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 mb-2">Today — Quick Mark</p>
                <div className="grid grid-cols-3 gap-2">
                  <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => quickMark('in')} data-testid="quick-mark-arrival-button" disabled={!selectedStudentId}>
                    <LogIn className="w-4 h-4 mr-1" /> Arrival Now
                  </Button>
                  <Button type="button" size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => quickMark('absent')} data-testid="quick-mark-absent-button" disabled={!selectedStudentId}>
                    <XCircle className="w-4 h-4 mr-1" /> Mark Absent
                  </Button>
                  <Button type="button" size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => quickMark('out')} data-testid="quick-mark-departure-button" disabled={!selectedStudentId}>
                    <LogOut className="w-4 h-4 mr-1" /> Departure Now
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Auto-fills current attendance status for today and keeps your roll up to date.</p>
              </div>
              <form onSubmit={markAttendance} className="space-y-3" data-testid="mark-attendance-form">
                <div><Label>Date</Label><Input type="date" value={attendanceForm.date} onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })} required data-testid="attendance-date-input" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={attendanceForm.status} onValueChange={(v) => setAttendanceForm({ ...attendanceForm, status: v })}>
                    <SelectTrigger data-testid="attendance-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Present</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                      <SelectItem value="leave">On Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Time In</Label><Input type="time" value={attendanceForm.time_in} onChange={(e) => setAttendanceForm({ ...attendanceForm, time_in: e.target.value })} data-testid="attendance-time-in-input" /></div>
                  <div><Label>Time Out</Label><Input type="time" value={attendanceForm.time_out} onChange={(e) => setAttendanceForm({ ...attendanceForm, time_out: e.target.value })} data-testid="attendance-time-out-input" /></div>
                </div>
                <div><Label>Note (optional)</Label><Textarea value={attendanceForm.note} onChange={(e) => setAttendanceForm({ ...attendanceForm, note: e.target.value })} rows={2} data-testid="attendance-note-input" /></div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-attendance-button" disabled={!selectedStudentId}><ClipboardCheck className="w-4 h-4 mr-2" />Save</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports">
      {/* Student selector for reports */}
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm font-medium">Select Student:</Label>
          <div className="flex flex-wrap gap-2">
            {students.length === 0 ? (
              <div className="text-sm text-slate-500">No students in the system yet.</div>
            ) : students.map((s) => (
              <Button
                key={s.id}
                variant={selectedStudentId === s.id ? 'default' : 'outline'}
                size="sm"
                className={`${selectedStudentId === s.id ? 'bg-blue-600 hover:bg-blue-700' : ''} flex items-center gap-2`}
                onClick={() => setSelectedStudentId(s.id)}
                data-testid={`teacher-report-student-select-${s.id}`}
              >
                <StudentAvatar photoId={s.student_photo_id} name={s.name} size="sm" testId={`teacher-report-student-avatar-${s.id}`} />
                {s.name}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Reports */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Reports</CardTitle>
            <CardDescription>Write monthly progress reports for your students (special needs template)</CardDescription>
          </div>
          {selectedStudentId && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => openCreateReport(selectedStudentId)} data-testid="new-report-button" disabled={!selectedStudentId}>
              <FileText className="w-4 h-4 mr-2" />New Report
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <div className="text-center py-6 text-slate-500">No reports yet</div>
          ) : (
            <div className="space-y-3" data-testid="teacher-reports-list">
              {reports.map((r, idx) => (
                <div key={r.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`teacher-report-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-900">{r.student_name} — {r.month}</h4>
                      <p className="text-xs text-slate-500">Rating: {r.overall_rating || '—'}/5 • Updated: {new Date(r.updated_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {statusBadge(r.status)}
                      {r.status === 'approved' && (
                        <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/reports/${r.id}/pdf`, '_blank')} data-testid={`download-report-pdf-${idx}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      {(r.status === 'draft' || r.status === 'pending' || r.status === 'rejected') && (
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => deleteReport(r.id)} data-testid={`delete-report-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                      )}
                      {(r.status === 'draft' || r.status === 'rejected') && (
                        <Button variant="outline" size="sm" onClick={() => openEditReport(r)} data-testid={`edit-report-${idx}`}><Edit2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="ot-reports">
      {/* Student selector for OT reports */}
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm font-medium">Select Student:</Label>
          <div className="flex flex-wrap gap-2">
            {students.length === 0 ? (
              <div className="text-sm text-slate-500">No students in the system yet.</div>
            ) : students.map((s) => (
              <Button
                key={s.id}
                variant={selectedStudentId === s.id ? 'default' : 'outline'}
                size="sm"
                className={`${selectedStudentId === s.id ? 'bg-blue-600 hover:bg-blue-700' : ''} flex items-center gap-2`}
                onClick={() => setSelectedStudentId(s.id)}
                data-testid={`teacher-ot-student-select-${s.id}`}
              >
                <StudentAvatar photoId={s.student_photo_id} name={s.name} size="sm" testId={`teacher-ot-student-avatar-${s.id}`} />
                {s.name}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* OT Reports */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>OT Progress Reports (3-Month)</CardTitle>
            <CardDescription>Occupational Therapy progress reports for your assigned students</CardDescription>
          </div>
          {selectedStudentId && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => openCreateOtReport(selectedStudentId)} data-testid="new-ot-report-button" disabled={!selectedStudentId}>
              <FileText className="w-4 h-4 mr-2" />New OT Report
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {otReports.length === 0 ? (
            <div className="text-center py-6 text-slate-500">No OT reports yet</div>
          ) : (
            <div className="space-y-3" data-testid="teacher-ot-reports-list">
              {otReports.map((r, idx) => (
                <div key={r.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`teacher-ot-report-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-900">{r.student_name} — OT Report</h4>
                      <p className="text-xs text-slate-500">Created: {new Date(r.created_at).toLocaleDateString()} • Updated: {new Date(r.updated_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {statusBadge(r.status)}
                      {(r.status === 'draft' || r.status === 'pending' || r.status === 'rejected') && (
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => deleteOtReport(r.id)} data-testid={`delete-ot-report-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                      )}
                      {(r.status === 'draft' || r.status === 'rejected') && (
                        <Button variant="outline" size="sm" onClick={() => openEditOtReport(r)} data-testid={`edit-ot-report-${idx}`}><Edit2 className="w-4 h-4" /></Button>
                      )}
                      {r.status === 'approved' && (
                        <>
                          {(user?.role === 'teacher' || user?.role === 'admin') && (
                            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/ot-reports/${r.id}/file/teacher`, '_blank')} data-testid={`download-ot-teacher-pdf-${idx}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {user?.role === 'admin' && (
                            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/ot-reports/${r.id}/file/admin`, '_blank')} data-testid={`download-ot-admin-pdf-${idx}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {user?.role === 'admin' && (
                            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/ot-reports/${r.id}/file/parent`, '_blank')} data-testid={`download-ot-parent-pdf-${idx}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {r.parent_feedback && (user?.role === 'teacher' || user?.role === 'admin') && (
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-100 rounded">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-700"><strong>Parent Feedback</strong> — {r.parent_feedback_by || 'Parent'}</p>
                          <p className="text-xs text-slate-500">Submitted: {new Date(r.parent_feedback_submitted_at).toLocaleString()}</p>
                        </div>
                        {r.parent_feedback_rating && <Badge className="bg-yellow-100 text-yellow-800">{r.parent_feedback_rating}/5</Badge>}
                      </div>
                      <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{r.parent_feedback}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

      </Tabs>

      {/* Report Dialog */}
      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingReport ? 'Edit' : 'Create'} Monthly Report</DialogTitle>
            <DialogDescription>Special needs progress template — save as draft or submit for admin approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Student</Label>
                <Select value={reportForm.student_id} onValueChange={(v) => setReportForm({ ...reportForm, student_id: v })} disabled={!!editingReport}>
                  <SelectTrigger data-testid="report-student-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Month</Label><Input type="month" value={reportForm.month} onChange={(e) => setReportForm({ ...reportForm, month: e.target.value })} data-testid="report-month-input" /></div>
              <div>
                <Label>Overall Rating (1-5)</Label>
                <Input type="number" min="1" max="5" value={reportForm.overall_rating || ''} onChange={(e) => setReportForm({ ...reportForm, overall_rating: parseInt(e.target.value) || null })} data-testid="report-rating-input" />
              </div>
            </div>
            {[
              { key: 'highlights', label: "Highlights / Teacher's Feedback", placeholder: 'Summary highlights, teacher observations and feedback...' },
                { key: 'special_event', label: 'Special Occasion / Event / Activity of the Month', placeholder: 'Describe any special event or activity this month...' },
                { key: 'therapy_notes', label: 'Behaviour Observation', placeholder: 'Observations of behaviour, social interaction, sensory response, attention...' },
                { key: 'areas_of_improvement', label: 'Areas Needing More Support', placeholder: 'Skills to focus on next month...' },
                { key: 'recommendations', label: 'Recommendations for Parents', placeholder: 'Home practice ideas, routines, activities...' },
            ].map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Textarea value={reportForm[f.key] || ''} onChange={(e) => setReportForm({ ...reportForm, [f.key]: e.target.value })} placeholder={f.placeholder} rows={3} data-testid={`report-${f.key}-input`} />
              </div>
            ))}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Pyramid of Learning</Label>
              <p className="text-xs text-slate-500 mb-3">Rate each area 1–5 (optional). Colours grade from red (needs support) to green (strong). Overall rating is calculated from the average of marked POL ratings.</p>
              <div className="text-xs text-slate-500 space-y-1 mb-3">
                <p><strong>Level 1</strong> Emerging – Requires extensive support</p>
                <p><strong>Level 2</strong> Developing – Requires frequent support</p>
                <p><strong>Level 3</strong> Progressing – Demonstrates skill with some support</p>
                <p><strong>Level 4</strong> Proficient – Demonstrates skill independently</p>
                <p><strong>Level 5</strong> Advanced – Consistently demonstrates skill beyond expectations</p>
              </div>
              <CnsPyramid
                ratings={reportForm.cns_ratings || {}}
                onChange={(next) => setReportForm({ ...reportForm, cns_ratings: next })}
                editable
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setReportDialog(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => saveReport(false)} data-testid="save-draft-button"><Save className="w-4 h-4 mr-2" />Save Draft</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => saveReport(true)} data-testid="submit-for-approval-button"><Send className="w-4 h-4 mr-2" />Submit for Approval</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* OT Report Dialog */}
      <Dialog open={otReportDialog} onOpenChange={setOtReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ot-report-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingOtReport ? 'Edit' : 'Create'} OT Progress Report (3-Month)</DialogTitle>
            <DialogDescription>Occupational Therapy progress report — save as draft or submit for admin approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Student</Label>
                <Input value={otReportForm.student_id && students.find(s => s.id === otReportForm.student_id)?.name || ''} readOnly className="bg-slate-100" />
              </div>
              <div>
                <Label>OT Session Start Date</Label>
                <Input type="date" value={otReportForm.ot_session_start_date || ''} onChange={(e) => setOtReportForm({ ...otReportForm, ot_session_start_date: e.target.value })} data-testid="ot-session-start-date-input" />
              </div>
              <div>
                <Label>Number of Sessions Attended (Optional)</Label>
                <Input type="number" value={otReportForm.sessions_attended || ''} onChange={(e) => setOtReportForm({ ...otReportForm, sessions_attended: parseInt(e.target.value) || '' })} data-testid="ot-sessions-attended-input" />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Current Student Condition (Observations)</Label>
              {[
                { key: 'current_condition_attention', label: 'Focus / Attention', placeholder: 'Observations of focus and attention...' },
                { key: 'current_condition_fine_motor', label: 'Fine Motor Skills', placeholder: 'Observations of fine motor skills...' },
                { key: 'current_condition_gross_motor', label: 'Gross Motor Skills', placeholder: 'Observations of gross motor skills...' },
                { key: 'current_condition_sensory', label: 'Sensory', placeholder: 'Sensory observations...' },
                { key: 'current_condition_adl', label: 'Activities of Daily Living (ADL)', placeholder: 'ADL observations...' },
                { key: 'current_condition_behaviour', label: 'Behaviour During Sessions', placeholder: 'Behaviour observations...' },
              ].map((f) => (
                <div key={f.key} className="mb-3">
                  <Label>{f.label}</Label>
                  <Textarea value={otReportForm[f.key] || ''} onChange={(e) => setOtReportForm({ ...otReportForm, [f.key]: e.target.value })} placeholder={f.placeholder} rows={2} data-testid={`ot-${f.key}-input`} />
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Short-Term Goals (3–5 objectives)</Label>
              {[0, 1, 2, 3, 4].map((idx) => (
                <Input
                  key={idx}
                  value={(otReportForm.short_term_goals || [])[idx] || ''}
                  onChange={(e) => {
                    const goals = [...(otReportForm.short_term_goals || [])];
                    goals[idx] = e.target.value;
                    setOtReportForm({ ...otReportForm, short_term_goals: goals.filter(g => g) });
                  }}
                  placeholder={`Goal ${idx + 1} (optional)`}
                  className="mb-2"
                  data-testid={`ot-short-term-goal-${idx}-input`}
                />
              ))}
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold">3-Month Progress</Label>
              <Textarea value={otReportForm.progress_3_months || ''} onChange={(e) => setOtReportForm({ ...otReportForm, progress_3_months: e.target.value })} placeholder="Describe the progress made over the past 3 months..." rows={3} data-testid="ot-progress-3-months-input" />
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Next 3-Month Plan</Label>
              <Textarea value={otReportForm.next_3_month_plan || ''} onChange={(e) => setOtReportForm({ ...otReportForm, next_3_month_plan: e.target.value })} placeholder="Intervention focus for the next 3 months..." rows={3} data-testid="ot-next-3-month-plan-input" />
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Home Program / Parent Recommendations (5 activities)</Label>
              {[0, 1, 2, 3, 4].map((idx) => (
                <Input
                  key={idx}
                  value={(otReportForm.home_program || [])[idx] || ''}
                  onChange={(e) => {
                    const programs = [...(otReportForm.home_program || [])];
                    programs[idx] = e.target.value;
                    setOtReportForm({ ...otReportForm, home_program: programs.filter(p => p) });
                  }}
                  placeholder={`Home activity ${idx + 1} (optional)`}
                  className="mb-2"
                  data-testid={`ot-home-program-${idx}-input`}
                />
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setOtReportDialog(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => saveOtReport(false)} data-testid="save-ot-draft-button"><Save className="w-4 h-4 mr-2" />Save Draft</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => saveOtReport(true)} data-testid="submit-ot-for-approval-button"><Send className="w-4 h-4 mr-2" />Submit for Approval</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherStudentsSection;
